import type { RecycledItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { errorMessage, get, post } from "../../api";
import { Alert, Button, Empty, Loading, PageHeader, Panel, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";

const PATHS = { contact: "contacts", job: "jobs", file: "files", proposal: "proposals" } as const;
const LABELS = { contact: "Contact", job: "Job", file: "File", proposal: "Proposal" } as const;

export function RecycleBinSettings() {
  const { me } = useSession();
  const qc = useQueryClient();
  const bin = useQuery({ queryKey: ["recycle-bin"], queryFn: () => get<RecycledItem[]>("/api/recycle-bin") });
  const restore = useMutation({
    mutationFn: (item: RecycledItem) => post(`/api/${PATHS[item.type]}/${item.id}/restore`),
    onSuccess: async () => {
      await qc.invalidateQueries();
    },
  });

  return (
    <div>
      <PageHeader title="Recycle bin" description="Deleted contacts, jobs, and files. Restoring a customer brings back the jobs deleted with them." />
      {restore.error && (
        <div className="mb-4">
          <Alert>{errorMessage(restore.error)}</Alert>
        </div>
      )}
      {bin.isLoading ? (
        <Loading />
      ) : bin.error ? (
        <Alert>{errorMessage(bin.error)}</Alert>
      ) : !bin.data?.length ? (
        <Panel>
          <Empty>The recycle bin is empty.</Empty>
        </Panel>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>What</th>
                <th className="hidden sm:table-cell">Deleted</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {bin.data.map((item) => (
                <tr key={`${item.type}-${item.id}`}>
                  <td>
                    <span className="mr-2 text-xs font-semibold tracking-wide text-muted uppercase">{LABELS[item.type]}</span>
                    {item.type === "file" ? (
                      <span className="font-medium">{item.label}</span>
                    ) : (
                      <Link to={`/${PATHS[item.type]}/${item.id}`} className="font-medium hover:text-brand">
                        {item.label}
                      </Link>
                    )}
                  </td>
                  <td className="hidden text-sm text-ink-soft sm:table-cell">
                    {formatDateTime(item.deletedAt, me?.company.timezone)}
                    {item.deletedBy && ` by ${item.deletedBy}`}
                  </td>
                  <td className="text-right">
                    <Button className="px-3 py-1.5" busy={restore.isPending && restore.variables === item} onClick={() => restore.mutate(item)}>
                      Restore
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
